import { FC, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { PriceTimeline } from 'features/price-timeline'

import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import listTicketsStore from 'entities/ticket/models/list-tickets/list-tickets-store'

import { TicketClassList } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { dateFormat } from 'shared/lib/dateFormat'

import {
  ComplexRouteSearchHeader,
  prepareComplexRouteHeaderData,
} from '../../complex-route-search-header'
import { HeaderUi } from '../../header-ui'

interface Props {
  isDifficultRoute?: boolean
  onGoBack?: () => void
  onSearchChangeCount?: () => void
  getListResultsByTimeline: (startDate: string, endDate: string) => void
}

export const Header: FC<Props> = observer(
  ({ isDifficultRoute, onGoBack, getListResultsByTimeline }) => {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const totalCountPassengers = findTicketFormStore.getCountOfPassengers()

    const currentTypeCabin = useMemo(
      () =>
        TicketClassList?.find(
          (item) =>
            item?.key === findTicketFormStore.formFindTicket?.cabin_class,
        ),
      [findTicketFormStore.formFindTicket?.cabin_class],
    )

    const onShowForm = () => {
      if (listTicketsStore?.loading) return

      if (isDifficultRoute) {
        navigate(RoutingPaths.avia.difficultRoute.path())
        return
      }

      navigate(RoutingPaths.avia.searchForm.path())
    }

    const onGoBackHandler = () => {
      navigate(RoutingPaths.path())
      onGoBack?.()
    }

    if (isDifficultRoute) {
      const complexHeaderData = prepareComplexRouteHeaderData(
        findTicketFormStore.formFindTicket,
        totalCountPassengers,
        currentTypeCabin?.name,
        t,
      )

      return (
        <ComplexRouteSearchHeader
          data={complexHeaderData}
          onGoBack={onGoBackHandler}
          onEditSearch={onShowForm}
        />
      )
    }

    const firstSegment = findTicketFormStore?.formFindTicket?.directions?.[0]
    const lastSegment = findTicketFormStore?.formFindTicket?.directions?.at(-1)

    let textDirection =
      dateFormat(firstSegment?.date || '', DATE_FORMAT_LIST.DATE_MONTH_SHORT) ||
      ''

    if (firstSegment?.back_date) {
      textDirection += ` – ${dateFormat(firstSegment?.back_date || '', DATE_FORMAT_LIST.DATE_MONTH_SHORT)}`
    }

    textDirection += ` • ${totalCountPassengers} ${t('passenger_short_name')} • ${t(currentTypeCabin?.name || '')}`

    return (
      <HeaderUi
        onGoBack={onGoBackHandler}
        onShowForm={onShowForm}
        textDirection={textDirection}
        isDifficultRoute={isDifficultRoute}
        segments={{
          first: firstSegment,
          last: lastSegment,
        }}
      >
        <PriceTimeline
          firstSegment={firstSegment}
          price={findTicketFormStore.dataForFilter?.timeline}
          getListResultsByTimeline={getListResultsByTimeline}
        />
      </HeaderUi>
    )
  },
)
